import type {
	PaginationDto,
	PaginationResponseDto
} from "core/dto";
import { ORDER_BY, ROLE } from "core/interfaces";
import { defineStore } from "pinia";
import type { PageMetaDto } from "shared/dto";

import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { getUserDefault } from "~/common/defaults/user.default";
import { serializeUserFilters } from "~/common/helpers/serialize-user-filters";
import type { GetAllUsersDto } from "~/common/interfaces/dto/users/get-all-users.dto";
import type { UpdateUserByIdDto } from "~/common/interfaces/dto/users/update-user-by-id.dto";
import type { User } from "~/common/interfaces/entities/user.interface";
import type { Filter } from "~/common/interfaces/filter.interface";
import type { Sort } from "~/common/interfaces/sort.interface";
import { useAuthStore } from "~/stores/auth";

export const useUsersStore = defineStore(
	"users",
	() => {
		const me = ref<User>(getUserDefault());
		const currentUser = ref<User>(getUserDefault());
		const users = ref<User[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<GetAllUsersDto & PaginationDto>();
		const tableFilter = ref<Filter[]>([
			{
				name: "Roles",
				value: {
					[ROLE.ADMIN]: false,
					[ROLE.MANAGER]: false
				}
			},
			{
				name: "Status",
				value: {
					active: false,
					inactive: false
				}
			}
		]);
		const tableSort = ref<Sort>({
			columns: ["email", "roles", "isActive"],
			orderBy: ["email", ORDER_BY.ASC]
		});

		const authStore = useAuthStore();

		const getMe = async (): Promise<User> => {
			me.value = await useNuxtApp().$apiFetch<User>("/users/me");
			if (!me.value.isActive) {
				authStore.signOut();
			}

			return me.value;
		};
		const getById = (id: number): Promise<User> => {
			return useNuxtApp().$apiFetch<User>(`/users/${id}`);
		};
		const patchById = (id: number, dto: UpdateUserByIdDto): Promise<User> => {
			return useNuxtApp().$apiFetch<User>(`/users/${id}`, {
				method: "PATCH",
				body: dto
			});
		};
		const getAll = async (
			dto: GetAllUsersDto = {},
			paginationDto?: Partial<PaginationDto>
		): Promise<Promise<PaginationResponseDto<User>>> => {
			const filter = serializeUserFilters(tableFilter.value);
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...dto,
				...getPaginationDefault(),
				...paginationDto,
				...filter,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<User>>("/users", {
				query: recentGetAllQuery.value
			});
			users.value = res.data;
			meta.value = res.meta;

			return res;
		};

		return {
			me,
			currentUser,
			users,
			meta,
			recentGetAllQuery,
			tableFilter,
			tableSort,

			getMe,
			getById,
			patchById,
			getAll
		};
	},
	{
		persist: {
			paths: ["tableFilter", "tableSort.orderBy"]
		}
	}
);
